/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { SelectionMode } from '@fluentui/react/lib/DetailsList';
import { IStackProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import { useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { AppStore } from 'src/core/stores/app/AppStore';
import { AppType } from 'src/core/stores/app/types';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { openUrlInNewTab } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { LanguageStudioSubHeader } from 'src/languageStudio/appShell/landingPage/components/LanguageStudioSubHeader';
import { ProjectsCommandBar } from 'src/languageStudio/appShell/landingPage/components/ProjectsCommandBar';
import { ProjectsTable } from 'src/languageStudio/appShell/landingPage/components/ProjectsTable';
import { pageClass } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

const messages = defineMessages({
    title: { id: 'languageStudio.projectsListPage.title', defaultMessage: 'All projects' },
    tabTitle: { id: 'languageStudio.projectsListPage.tabTitle', defaultMessage: 'Language Studio: Projects' },
    noProjects: { id: 'languageStudio.projectsListPage.noProjects', defaultMessage: "You don't have any projects yet." },
    deletingProjectTitle: { id: 'languageStudio.projectsListPage.deletingProjectTitle', defaultMessage: 'Delete selected project(s)' },
    deletingProjectBody: {
        id: 'languageStudio.projectsListPage.deletingProjectBody',
        defaultMessage: 'Are you sure you want to delete the selected project(s)?'
    },
    failedDeletingProjects: {
        id: 'languageStudio.projectsListPage.failedDeletingProjects',
        defaultMessage: '{failedCount} projects failed.'
    },
    deleteProjectStart: {
        id: 'languageStudio.projectsListPage.deleteProjectStart',
        defaultMessage: 'Deleting project(s) ...'
    },
    deleteProjectSuccess: {
        id: 'languageStudio.projectsListPage.deleteProjectSuccess',
        defaultMessage: 'Delete project(s) completed'
    },
    deleteProjectFailed: {
        id: 'languageStudio.projectsListPage.deleteProjectFailed',
        defaultMessage: 'Delete project(s) failed'
    }
});

const { styleComponent } = getStylist('ProjectListPage');
const ProjectListContainer = styleComponent<IStackProps>(Stack)('ProjectListContainer', { overflow: 'hidden' });

export const ProjectsListPage = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const [query, setQuery] = React.useState('');
    const [activeTypes, setActiveTypes] = React.useState<AppType[]>([]);
    const [sortKey, setSortKey] = React.useState<SortKeyType<AppStore>>({ key: 'modifiedDate', order: 'desc' });
    const { selection, selectedItems } = useSelection<AppStore>();
    const appsLocalStoreHouse = useLocalStore<ResourceStoreHouse<AppStore>>(createResourceStoreHouse);

    React.useEffect(() => {
        if (rootStore.serviceKey.value) {
            dispatcher.dispatch('loadLanguageStudioProjects', { localStore: appsLocalStoreHouse });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id]);

    const projects = appsLocalStoreHouse.items
        .filter(app => !activeTypes.length || activeTypes.some(t => t === app.appType))
        .filter(
            v =>
                (v.appType as string) !== 'CustomConversationSummarization' &&
                (v.appType as string) !== 'CustomAbstractiveSummarization' &&
                (v.appType as string) !== 'CustomTextSentiment' &&
                (v.appType as string) !== 'CustomHealthcare'
        );
    const orderedProjects = orderBy(
        substringSearchStrategy(projects, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const areSelectedProjectsClu = selectedItems.every(
        project =>
            project.appType === 'clu' ||
            project.appType === 'conversation' ||
            project.appType === 'Conversation' ||
            project.appType === 'Orchestration'
    );
    const areSelectedProjectsCt = selectedItems.every(
        project =>
            project.appType === 'CustomEntityRecognition' ||
            project.appType === 'CustomSingleLabelClassification' ||
            project.appType === 'CustomMultiLabelClassification'
    );
    const areSelectedProjectsQna = selectedItems.every(project => project.appType === 'qnaMaker');

    const [isDeleteProjectModalHidden, setIsDeleteProjectModalHidden] = React.useState(true);
    const [isDeleteEnabled, setIsDeleteEnabled] = React.useState(false);

    React.useEffect(() => {
        if (selectedItems.length && (areSelectedProjectsClu || areSelectedProjectsCt || areSelectedProjectsQna)) {
            setIsDeleteEnabled(true);
        } else {
            setIsDeleteEnabled(false);
        }
    }, [selectedItems]);

    const defaultOperationsTracker = useProgressTracker({
        onComplete: () => {
            setIsDeleteProjectModalHidden(true);
            selection.setAllSelected(false);
        }
    });

    const handleDeleteProject = () => {
        if (areSelectedProjectsClu) {
            handleDeleteCluProject();
        } else if (areSelectedProjectsCt) {
            handleDeleteCTProject();
        } else {
            handleDeleteQnaProject();
        }
        selection.setAllSelected(false);
    };

    const handleDeleteCluProject = () => {
        dispatcher.dispatch('deleteCluProjects', {
            toastData: {
                running: { title: localize(messages.deleteProjectStart) },
                completed: { title: localize(messages.deleteProjectSuccess) },
                failed: { title: localize(messages.deleteProjectFailed) }
            },
            tracker: defaultOperationsTracker,
            appsLocalStoreHouse,
            projectNames: selectedItems.map(item => item.name),
            generateMultiFailureMessage: (failedCount: number) => localize(messages.failedDeletingProjects, { failedCount })
        });
    };

    const handleDeleteCTProject = () => {
        dispatcher.dispatch('deleteCustomTextProject', {
            toastData: {
                running: { title: localize(messages.deleteProjectStart) },
                completed: { title: localize(messages.deleteProjectSuccess) },
                failed: { title: localize(messages.deleteProjectFailed) }
            },
            tracker: defaultOperationsTracker,
            appsLocalStoreHouse,
            projectNames: selectedItems.map(item => item.name),
            generateMultiFailureMessage: (failedCount: number) => localize(messages.failedDeletingProjects, { failedCount })
        });
    };

    const handleDeleteQnaProject = () => {
        dispatcher.dispatch('deleteQnaMakerProjects', {
            toastData: {
                running: { title: localize(messages.deleteProjectStart) },
                completed: { title: localize(messages.deleteProjectSuccess) },
                failed: { title: localize(messages.deleteProjectFailed) }
            },
            tracker: defaultOperationsTracker,
            appsLocalStoreHouse,
            projectIds: selectedItems.map(selectedItem => selectedItem.id)
        });
    };

    const handleAppClick = (app: AppStore) => {
        switch (app.appType) {
            case 'qnaMaker':
                openUrlInNewTab(routeTable.qnaMaker.projectPage(app.id));
                break;
            case 'CustomEntityRecognition':
            case 'CustomMultiLabelClassification':
            case 'CustomSingleLabelClassification':
                openUrlInNewTab(routeTable.customText.projectPage(app.id, app.appType as CustomTextProjectKind));
                break;
            case 'clu':
                openUrlInNewTab(routeTable.clu.projectPage(app.id));
                break;
            case 'Orchestration':
                openUrlInNewTab(routeTable.orchestration.projectPage(app.id));
                break;
            default:
        }
    };

    return (
        <>
            <StackItem tokens={{ padding: '0 0 8px 0' }}>
                <LanguageStudioSubHeader marginLeft="9.5%" />
            </StackItem>

            <Page size="container" className={pageClass} title={localize(messages.tabTitle)} pageName="LanguageStudioProjectsList">
                <EditableTitle title={localize(messages.title)} titleTextProps={{ fontWeight: 600 }} />

                <ProjectListContainer tokens={{ childrenGap: 10 }}>
                    <ProjectsCommandBar
                        onQueryChange={setQuery}
                        filterProjectTypes={activeTypes}
                        isDeleteEnabled={isDeleteEnabled}
                        onChangeFilterProjectTypes={setActiveTypes}
                        displayedItemsCount={orderedProjects.length}
                        isLoading={appsLocalStoreHouse.tracker.isRunning}
                        onDeleteProject={() => setIsDeleteProjectModalHidden(false)}
                    />
                    <ProjectsTable
                        sortKey={sortKey}
                        selection={selection}
                        isRowClickable={false}
                        items={orderedProjects}
                        onAppClick={handleAppClick}
                        selectionMode={SelectionMode.multiple}
                        overallItemsCount={orderedProjects.length}
                        emptyPlaceholder={localize(messages.noProjects)}
                        areItemsFiltered={Boolean(query || activeTypes.length)}
                        isLoading={appsLocalStoreHouse.tracker.isRunning || defaultOperationsTracker.isRunning}
                        onSortColumn={c => setSortKey({ key: c.key as keyof AppStore, order: c.isSortedDescending ? 'asc' : 'desc' })}
                    />
                </ProjectListContainer>

                <DeletePromptModal
                    onDelete={handleDeleteProject}
                    disabled={defaultOperationsTracker.isRunning}
                    hidden={isDeleteProjectModalHidden}
                    onDismiss={() => setIsDeleteProjectModalHidden(true)}
                    dialogContentProps={{ title: localize(messages.deletingProjectTitle) }}>
                    <LuisText>{localize(messages.deletingProjectBody)}</LuisText>
                </DeletePromptModal>
            </Page>
        </>
    );
});
